import { useCallback, useMemo, useState } from 'react';

import { useParams } from 'react-router';

import { Box, Stack, Typography } from '@mui/material';

import sortBy from 'lodash/sortBy';

import * as Sentry from '@sentry/react';
import { IS_PROD } from '@/constants';
import { ChainDeploymentStatus, ChainEnvironmentReleaseStatus, DeploymentStatus } from '@/graphql';
import useFetchWithAuthToken from '@/hooks/auth/useFetchWithAuthToken';
import useIsHijacked from '@/hooks/useIsHijacked';
import useIsTeamsEnabled from '@/hooks/useIsTeamsEnabled';
import { usePromiseSnackbar } from '@/hooks/usePromiseSnackbar';
import { WorkspaceSections } from '@/pages/Workspace/types';
import { BasetenPageEnum, usePageSwitcher } from '@/routes';
import useRegisterBreadcrumbs from '@/sections/Billip/BillBar/useRegisterBreadcrumbs';
import useConfirmationDialog from '@/sections/Dialog/Confirmation';
import useSafeguardConfirmationDialog from '@/sections/Dialog/SafeguardConfirmation';
import { ProductionReleasesForChainEnvironmentFragment } from '@/sections/Workspace/__generated__/fragments.generated';
import {
  ChainDocument,
  ChainQuery,
  ChainStableEnvironmentsDocument,
  ChainStableEnvironmentsQueryHookResult,
  useChainQuery,
  useChainStableEnvironmentQuery,
  useChainStableEnvironmentsQuery,
  namedOperations as workspaceNamedOperations,
} from '@/sections/Workspace/__generated__/queries.generated';
import { useCheckHasAccessToEnvironment } from '@/sections/Workspace/hooks';
import { TabsEnum } from '@/sections/Workspace/types';
import { PROD_ENV_NAME, getChainletUrl, getDefaultDeployment } from '@/sections/Workspace/utils';
import { getFirstGraphQLError } from '@/utils/errorHandling';
import { InMemoryCache, useApolloClient } from '@apollo/client';
import produce from 'immer';
import moment from 'moment';
import { useRecoilState } from 'recoil';

import {
  ChainDeploymentFragment,
  ChainOverviewFragment,
  ChainletFragment,
  ChainletMetaFragment,
  PromoteChainDeploymentArgsFragment,
} from './__generated__/fragments.generated';
import {
  useActivateChainDeploymentMutation,
  useCancelChainDeploymentPromotionMutation,
  useCreateChainStableEnvironmentMutation,
  useDeactivateChainDeploymentMutation,
  useDeleteChainDeploymentMutation,
  useDeleteChainMutation,
  useDeleteChainStableEnvironmentMutation,
  usePromoteChainDeploymentMutation,
  usePromoteChainDeploymentToEnvironmentMutation,
} from './__generated__/mutations.generated';
import {
  ChainDeploymentsDocument,
  ChainDeploymentsQuery,
  UpdatePrimaryDeploymentInCacheDocument,
  UpdatePrimaryDeploymentInCacheQuery,
  useChainDeploymentS3DownloadUrlLazyQuery,
  useChainDeploymentsQuery,
  useChainletsQuery,
  useCurrentUsageForChainQuery,
  useProductionReleasesForChainEnvironmentQuery,
} from './__generated__/queries.generated';
import {
  apiEndpointId,
  autoScalingVersionId,
  configurePromotionEnvId,
  playgroundId,
  renameDeploymentId,
} from './atoms';
import usePromoteConfirmation from './sections/PromoteConfirmation';
import { DialogId, MatchParams, UseStableEnvironments } from './types';
import { getEntrypointChainlet } from './utils';

function useUrlId() {
  const { chainId, tabEntityId, chainletId: chainletIdFromUrl, tabId } = useParams<MatchParams>();
  const goToPage = usePageSwitcher();
  const { stableEnvironments } = useStableEnvironments();
  const { deployments } = useChainDeployments();

  const env = stableEnvironments.find((env) => env.id === tabEntityId);

  const deploymentId =
    tabId === TabsEnum.Environments ? env?.currentChainDeployment?.id : tabEntityId;

  const chainletId =
    tabId === TabsEnum.Environments
      ? getEntrypointChainlet(env?.currentChainDeployment)?.id
      : chainletIdFromUrl;

  const setUrlId = useCallback(
    (urlId: DialogId) => {
      goToPage(BasetenPageEnum.Chain, {
        chainId,
        tabId,
        ...urlId,
      });
    },
    [goToPage, chainId, tabId],
  );

  const defaultDeployment = getDefaultDeployment(deployments);
  const entrypointChainlet = getEntrypointChainlet(defaultDeployment);

  return {
    urlId: {
      tabEntityId: deploymentId || defaultDeployment?.id,
      chainletId: chainletId || entrypointChainlet?.id,
    },
    setUrlId,
  };
}

function useChain() {
  const { chainId } = useParams<MatchParams>();

  return useChainQuery({
    variables: { id: chainId },
    fetchPolicy: 'cache-first',
  });
}

function useAutoScalingVersionId() {
  const { chainId } = useParams<MatchParams>();

  return useRecoilState(autoScalingVersionId(chainId));
}

function usePlaygroundId() {
  const { chainId } = useParams<MatchParams>();

  return useRecoilState(playgroundId(chainId));
}

function useAPIEndpointId() {
  const { chainId } = useParams<MatchParams>();

  return useRecoilState(apiEndpointId(chainId));
}

function useRenameDeploymentId() {
  const { chainId } = useParams<MatchParams>();

  return useRecoilState(renameDeploymentId(chainId));
}

function useDeleteChain(name: string, id: string, doNotRedirect?: boolean) {
  const promiseSnackbar = usePromiseSnackbar();
  const goToPage = usePageSwitcher();
  const safeguardConfirmationDialog = useSafeguardConfirmationDialog();

  const [deleteChain] = useDeleteChainMutation();

  return useCallback(async () => {
    const failedText = `An unexpected error occurred. Chain ${name} could not be deleted.`;
    const variables = { id };

    const confirmed = await safeguardConfirmationDialog({
      title: 'Delete Chain',
      okText: 'Delete',
      prompt:
        'Are you sure you want to delete this chain and all of its deployments? This cannot be undone.',
      validationText: name,
      validationHint: 'Chain name',
    });

    if (confirmed) {
      promiseSnackbar(
        deleteChain({
          variables,
          onCompleted() {
            if (!doNotRedirect) {
              goToPage(BasetenPageEnum.Workspace, {
                section: WorkspaceSections.Overview,
              });
            }
          },
        }),
        'The chain was successfully deleted.',
        failedText,
      );
    }
  }, [
    deleteChain,
    goToPage,
    name,
    promiseSnackbar,
    id,
    safeguardConfirmationDialog,
    doNotRedirect,
  ]);
}

function useDeleteChainDeployment(
  id: string,
  chainId: string,
  chainName: string,
  deployments: ChainDeploymentFragment[],
) {
  const promiseSnackbar = usePromiseSnackbar();
  const goToPage = usePageSwitcher();
  const [playgroundId, setPlaygroundId] = usePlaygroundId();
  const confirmationDialog = useConfirmationDialog();
  const safeguardConfirmationDialog = useSafeguardConfirmationDialog();

  const [deleteChainDeployment] = useDeleteChainDeploymentMutation({
    onCompleted() {
      if (playgroundId?.tabEntityId === id) {
        setPlaygroundId(null);
      }
    },
    update: (cache: InMemoryCache, _, { variables: { id } }) => {
      cache.updateQuery(
        {
          broadcast: false,
          query: ChainDeploymentsDocument,
          variables: {
            id: chainId,
          },
        },
        produce((state: ChainDeploymentsQuery) => {
          if (state.chain?.deployments) {
            state.chain.deployments = state.chain.deployments.filter(
              (deployment) => deployment.id !== id,
            );
          }
        }),
      );
    },
  });

  return useCallback(async () => {
    const failedText = `An unexpected error occurred. #${id} could not be deleted.`;
    const variables = { id };
    const remainingDeployments = deployments.filter(({ id: deploymentId }) => id !== deploymentId);

    if (remainingDeployments.length === 0) {
      const confirmed = await safeguardConfirmationDialog({
        title: 'Delete Chain',
        okText: 'Delete',
        prompt: `Deleting the only deployment of this chain will delete the entire chain.
              Any applications using this chain will stop working as expected. You cannot undo this action.
              Are you sure you want to delete this chain?`,
        validationText: chainName,
        validationHint: 'Chain name',
      });

      if (confirmed) {
        promiseSnackbar(
          deleteChainDeployment({
            variables,
            onCompleted(data) {
              if (data?.deleteChainDeployment?.ok) {
                goToPage(BasetenPageEnum.Workspace, {
                  section: WorkspaceSections.Overview,
                });
              }
            },
          }),
          'The chain was successfully deleted.',
          failedText,
        );
      }
    } else {
      const confirmed = await confirmationDialog({
        title: 'Delete deployment',
        okText: 'Delete',
        prompt: "Are you sure you want to delete this deployment? You can't undo this action.",
        okColor: 'error',
      });

      if (confirmed) {
        promiseSnackbar(
          deleteChainDeployment({ variables }),
          `#${id} was deleted successfully.`,
          failedText,
        );
      }
    }
  }, [
    deleteChainDeployment,
    goToPage,
    safeguardConfirmationDialog,
    deployments,
    chainName,
    promiseSnackbar,
    id,
    confirmationDialog,
  ]);
}

function useDeactivatChainDeployment(id: string, chainId: string) {
  const promiseSnackbar = usePromiseSnackbar();
  const confirmationDialog = useConfirmationDialog();

  const [deactivateChainDeployment, { loading }] = useDeactivateChainDeploymentMutation({
    optimisticResponse({ id }) {
      return {
        deactivateChainDeployment: {
          ok: true,
          primaryChainDeployment: {
            id,
            isPrimary: false,
          },
          chainDeployment: {
            id,
            status: ChainDeploymentStatus.DeactivatingModel,
          },
        },
      };
    },
    update: (
      cache: InMemoryCache,
      {
        data: {
          deactivateChainDeployment: { chainDeployment, primaryChainDeployment },
        },
      },
      { variables: { id } },
    ) => {
      if (primaryChainDeployment?.isPrimary) {
        updatePrimaryInCache(cache, chainId, primaryChainDeployment.id);
      }

      const status =
        chainDeployment.status === ChainDeploymentStatus.DeactivatingModel
          ? ChainDeploymentStatus.DeactivatingModel
          : ChainDeploymentStatus.DeactivatedModel;

      updateChainDeploymentStatusInCache(cache, chainId, id, status);
    },
  });

  const isHijacked = useIsHijacked();

  const action = useCallback(async () => {
    const confirmed = await confirmationDialog({
      title: `Are you sure you want to deactivate #${id}?`,
      okText: `Yes, deactivate #${id}`,
      prompt: (
        <Stack gap={1}>
          <Typography>
            This chain deployment will be deactivated immediately so future requests will fail. You
            can reactivate this chain deployment any time.
          </Typography>
          {IS_PROD && isHijacked && (
            <Box sx={{ backgroundColor: 'red', color: 'white', fontWeight: 'bold', padding: 1 }}>
              You are currently hijacking another user. Make sure you really mean to deactivate this
              chain deployment.
            </Box>
          )}
        </Stack>
      ),
      okColor: 'error',
    });

    if (confirmed) {
      promiseSnackbar(
        deactivateChainDeployment({ variables: { id } }),
        `#${id} was deactivated successfully.`,
        `An unexpected error occurred. #${id} could not be deactivated.`,
        false,
        {
          variant: 'default',
        },
      );
    }
  }, [promiseSnackbar, deactivateChainDeployment, id, confirmationDialog, isHijacked]);

  return useMemo(() => ({ deactivateChainDeployment: action, loading }), [action, loading]);
}

/**
 * Helper function that updates the cache to update the primary deployment of a chain after
 * any mutation that changes the primary version
 */
function updatePrimaryInCache(
  cache: InMemoryCache,
  chainId: string,
  primaryDeploymentId: string,
): void {
  let sawPrimary = false;

  cache.updateQuery(
    {
      broadcast: false,
      query: UpdatePrimaryDeploymentInCacheDocument,
      variables: {
        id: chainId,
      },
    },
    produce(({ chain: { deployments } }: UpdatePrimaryDeploymentInCacheQuery) => {
      for (const deployment of deployments) {
        deployment.isPrimary = deployment.id === primaryDeploymentId;
        sawPrimary ||= true;
      }
    }),
  );
  if (!sawPrimary) {
    const error = new Error(`Primary deployment ${primaryDeploymentId} not found`);
    // eslint-disable-next-line no-console
    console.error(error);
    Sentry.captureException(error);
  }
}

/**
 * Helper function that updates the cache to update the inProgressEnvironmentRelease of a particular chain deployment
 */
function updateReleaseStatusInCache(
  cache: InMemoryCache,
  chainId: string,
  status: ChainEnvironmentReleaseStatus,
  envName: string,
) {
  if (envName === PROD_ENV_NAME) {
    cache.updateQuery(
      {
        broadcast: false,
        query: ChainDocument,
        variables: {
          id: chainId,
        },
      },
      produce((state: ChainQuery) => {
        if (state.chain.inProgressProductionRelease) {
          state.chain.inProgressProductionRelease.status = status;
        }
      }),
    );
  } else {
    cache.updateQuery(
      {
        broadcast: false,
        query: ChainStableEnvironmentsDocument,
        variables: {
          chainId,
        },
      },
      produce((data: ChainStableEnvironmentsQueryHookResult['data']) => {
        const env = data.chainStableEnvironments.find((env) => env.name === envName);

        if (env) {
          env.inProgressRelease.status = status;
        }
      }),
    );
  }
}

/**
 * Helper function that updates the cache to update the status of a particular chain deployment
 * after any mutation that changes the model status
 */
function updateChainDeploymentStatusInCache(
  cache: InMemoryCache,
  chainId: string,
  chainDeploymentId: string,
  status: ChainDeploymentStatus,
): void {
  cache.updateQuery(
    {
      broadcast: false,
      query: ChainDeploymentsDocument,
      variables: {
        id: chainId,
      },
    },
    produce((state: ChainDeploymentsQuery) => {
      const deployment = state?.chain.deployments.find((v) => v.id === chainDeploymentId);

      if (!deployment?.status) {
        return;
      }

      deployment.status = status;
      // update chainlets status
      deployment.chainlets?.forEach((chainlet) => {
        if (chainlet.oracleVersion) {
          chainlet.oracleVersion.currentModelDeploymentStatus.status =
            status as unknown as DeploymentStatus;
        }
      });
    }),
  );
}

function updateChainletModelStatusInCache(
  cache: InMemoryCache,
  chainId: string,
  deploymentId: string,
  chainletId: string,
  status: DeploymentStatus,
): void {
  cache.updateQuery(
    {
      broadcast: false,
      query: ChainDocument,
      variables: {
        id: chainId,
      },
    },
    produce((state) => {
      const deployment = state.chain.deployments.find(
        (d: ChainDeploymentFragment) => d.id === deploymentId,
      );
      const chainlet = deployment?.chainlets.find((c: ChainletFragment) => c.id === chainletId);
      const currentModelDeploymentStatus = chainlet?.oracleVersion?.currentModelDeploymentStatus;

      if (!currentModelDeploymentStatus) {
        return;
      }

      currentModelDeploymentStatus.status = status;
    }),
  );
}

function updateChainDeploymentNameInCache(
  cache: InMemoryCache,
  chainId: string,
  chainDeploymentId: string,
  name: string,
): void {
  cache.updateQuery(
    {
      broadcast: false,
      query: ChainDeploymentsDocument,
      variables: {
        id: chainId,
      },
    },
    produce((state: ChainDeploymentsQuery) => {
      const deployment = state?.chain.deployments.find((d) => d.id === chainDeploymentId);

      if (deployment) {
        deployment.name = name;
      }
    }),
  );
}

function useActivateChainDeployment(id: string, chainId: string) {
  const promiseSnackbar = usePromiseSnackbar();

  const [activateChainDeployment, { loading }] = useActivateChainDeploymentMutation({
    update: (
      cache: InMemoryCache,
      {
        data: {
          activateChainDeployment: { primaryChainDeployment },
        },
      },
      { variables: { id } },
    ) => {
      if (primaryChainDeployment?.isPrimary) {
        updatePrimaryInCache(cache, chainId, primaryChainDeployment.id);
      }
      updateChainDeploymentStatusInCache(cache, chainId, id, ChainDeploymentStatus.DeployingModel);
    },
  });

  const action = useCallback(() => {
    promiseSnackbar(
      activateChainDeployment({ variables: { id } }),
      `Deployment ${id} is deploying.`,
      `An unexpected error occurred. #${id} could not be activated.`,
      false,
    );
  }, [activateChainDeployment, promiseSnackbar, id]);

  return useMemo(() => ({ activateChainDeployment: action, loading }), [action, loading]);
}

function usePromoteChainDeployment({
  id,
  chain: { id: chainId },
}: PromoteChainDeploymentArgsFragment) {
  const promiseSnackbar = usePromiseSnackbar();

  const [promoteChainDeployment, { loading }] = usePromoteChainDeploymentMutation({
    update: (
      cache: InMemoryCache,
      {
        data: {
          updateChainDeployment: { primaryChainDeployment },
        },
      },
      { variables: { id } },
    ) => {
      if (primaryChainDeployment) {
        updatePrimaryInCache(cache, chainId, primaryChainDeployment.id);
      }
    },
  });

  const action = useCallback(() => {
    promiseSnackbar(
      promoteChainDeployment({ variables: { id } }),
      `#${id} is promoting to production.`,
      `An unexpected error occurred. #${id} could not be promoted.`,
      false,
    );
  }, [promoteChainDeployment, promiseSnackbar, id]);

  return useMemo(() => ({ promoteChainDeployment: action, loading }), [action, loading]);
}

function useCurrentUsage() {
  const { chainId } = useParams<MatchParams>();

  return useCurrentUsageForChainQuery({
    variables: {
      id: chainId,
    },
    fetchPolicy: 'cache-and-network',
  });
}

function useWakeUpChainlet(
  chainDeployment: ChainDeploymentFragment,
  chainlet: ChainletMetaFragment,
  chainHostname: string,
  shouldUpdateChainDeploymentInCache = false,
) {
  const promiseSnackbar = usePromiseSnackbar();
  const [loading, setLoading] = useState(false);
  const { chainId } = useParams<MatchParams>();
  const fetchWithAuthToken = useFetchWithAuthToken();

  const client = useApolloClient();
  const chainEntity = chainlet.isEntrypoint
    ? `Chain deployment ${chainDeployment.id}`
    : `Chainlet #${chainlet.id}`;

  const action = useCallback(() => {
    promiseSnackbar(
      (async () => {
        setLoading(true);

        try {
          const resp = await fetchWithAuthToken(
            getChainletUrl(chainDeployment, chainlet, 'wake', chainHostname),
            {
              method: 'POST',
            },
          );

          if (!resp.ok) {
            throw new Error(`Unexpected response: ${resp.status}`);
          }

          if (shouldUpdateChainDeploymentInCache) {
            updateChainDeploymentStatusInCache(
              client.cache as InMemoryCache,
              chainId,
              chainDeployment.id,
              ChainDeploymentStatus.ScalingFromZero,
            );
          } else {
            updateChainletModelStatusInCache(
              client.cache as InMemoryCache,
              chainId,
              chainDeployment.id,
              chainlet.id,
              DeploymentStatus.ScalingFromZero,
            );
          }
        } finally {
          setLoading(false);
        }
      })(),
      `${chainEntity} is waking up.`,
      `An unexpected error occurred. ${chainEntity} could not be woken up.`,
      false,
    );
  }, [
    promiseSnackbar,
    chainId,
    chainDeployment,
    fetchWithAuthToken,
    chainlet,
    client.cache,
    shouldUpdateChainDeploymentInCache,
    chainEntity,
    chainHostname,
  ]);

  return useMemo(() => ({ wakeUpChainlet: action, loading }), [action, loading]);
}

function useChainlets(deploymentId: string, poll = true) {
  const { data, loading } = useChainletsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      deploymentId,
    },
    initialFetchPolicy: 'cache-and-network',
    pollInterval: poll ? 10000 : null,
  });

  return {
    chainlets: data?.chainDeployment.chainlets || [],
    loading,
  };
}

// stable environments

function useStableEnvironments(
  poll = false,
  providedChainId?: string,
  skip = false,
): UseStableEnvironments {
  const { chainId } = useParams<MatchParams>();
  const { data, loading } = useChainStableEnvironmentsQuery({
    variables: {
      chainId: providedChainId || chainId,
    },
    fetchPolicy: 'cache-first',
    pollInterval: poll ? 10000 : null,
    skip,
  });

  return {
    stableEnvironments: sortBy(data?.chainStableEnvironments, (env) =>
      moment(env.created).valueOf(),
    ).reverse(),
    mapVersionsToEnvironments:
      data?.chainStableEnvironments.reduce(
        (acc, env) =>
          env.currentChainDeployment
            ? {
                ...acc,
                [env.currentChainDeployment.id]: env,
              }
            : acc,
        {},
      ) || {},
    loading,
  };
}

function useCreateStableEnvironment() {
  const { chainId } = useParams<MatchParams>();
  const promiseSnackbar = usePromiseSnackbar();
  const [createStableEnvironment, { loading }] = useCreateChainStableEnvironmentMutation();
  const isTeamsEnabled = useIsTeamsEnabled();

  const action = useCallback(
    async (name: string, successCb?: () => void) => {
      return promiseSnackbar(
        createStableEnvironment({
          variables: {
            name,
            chainId,
          },
          refetchQueries: isTeamsEnabled ? ['Environments'] : [],
          onCompleted: successCb,
          update: (cache: InMemoryCache, { data }) => {
            const { id, name } = data?.createChainStableEnvironment;
            cache.updateQuery(
              {
                broadcast: false,
                query: ChainStableEnvironmentsDocument,
                variables: {
                  chainId,
                },
              },
              produce((data: ChainStableEnvironmentsQueryHookResult['data']) => {
                data.chainStableEnvironments.push({
                  __typename: 'ChainStableEnvironmentType',
                  id,
                  name,
                  currentChainDeployment: null,
                  created: null,
                  modified: null,
                  inProgressRelease: null,
                  rampUpDurationSeconds: 0,
                  rampUpWhilePromoting: false,
                  redeployOnPromotion: false,
                });
              }),
            );
          },
        }),
        `Environment "${name}" was created successfully.`,
        handleCreateStableEnvironmentError(name),
        false,
        {},
        isTeamsEnabled,
      );
    },
    [promiseSnackbar, createStableEnvironment, chainId, isTeamsEnabled],
  );

  return useMemo(() => ({ createStableEnvironment: action, loading }), [action, loading]);
}

function handleCreateStableEnvironmentError(name: string): (error: Error) => string {
  return (error: Error): string => {
    const defaultMessage = 'An unexpected error occurred. Stable environment could not be created.';

    const err = getFirstGraphQLError(error);
    if (err == null) {
      return defaultMessage;
    }

    if (
      err.extensions?.code === 'RESOURCE_VALIDATION_FAILED' &&
      (err.extensions?.description as string)?.includes?.('Name already exists')
    ) {
      return `Environment with name "${name}" already exists. Please try a different name.`;
    }

    return defaultMessage;
  };
}

function useGetDeploymentAssociatedEnv(deploymentId: string) {
  const { stableEnvironments } = useStableEnvironments();

  return stableEnvironments.find((env) => env.currentChainDeployment?.id === deploymentId);
}

function usePromoteDeploymentToEnvironment(
  deployment: ChainDeploymentFragment,
  prodDeployment: ChainDeploymentFragment,
  chain: ChainOverviewFragment,
) {
  const promoteConfirmation = usePromoteConfirmation();
  const promiseSnackbar = usePromiseSnackbar();
  const deploymentAssociatedEnv = useGetDeploymentAssociatedEnv(deployment.id);
  const { stableEnvironments } = useStableEnvironments(false, chain.id);
  const shouldImmediatelyPromote =
    !deployment.isDraft && !deployment.isPrimary && !deploymentAssociatedEnv;
  const checkHasAccess = useCheckHasAccessToEnvironment(chain.team.id, chain.team.currentUser.role);

  const [promoteDeploymentToEnvironment] = usePromoteChainDeploymentToEnvironmentMutation({
    refetchQueries: [
      workspaceNamedOperations.Query.Chain,
      workspaceNamedOperations.Query.ChainStableEnvironments,
    ],
    update: (
      cache: InMemoryCache,
      _,
      { variables: { chainDeploymentId, stableEnvironmentName } },
    ) => {
      // we want to update StableEnvironmentDocument to refetch the stable environments
      cache.updateQuery(
        {
          broadcast: false,
          query: ChainStableEnvironmentsDocument,
          variables: {
            chainId: chain.id,
          },
        },
        produce((data: ChainStableEnvironmentsQueryHookResult['data']) => {
          const env = data.chainStableEnvironments.find(
            (env) => env.name === stableEnvironmentName,
          );
          if (shouldImmediatelyPromote) {
            env.currentChainDeployment = deployment;
          }
        }),
      );
    },
  });

  const [promoteDeploymentToProd] = usePromoteChainDeploymentMutation({
    refetchQueries: [workspaceNamedOperations.Query.Chain],
    update: (
      cache: InMemoryCache,
      {
        data: {
          updateChainDeployment: { primaryChainDeployment },
        },
      },
      { variables: { id } },
    ) => {
      if (primaryChainDeployment) {
        updatePrimaryInCache(cache, chain.id, primaryChainDeployment.id);
      }
    },
  });

  return useCallback(async () => {
    if (!deployment) {
      return;
    }

    const { confirmed, env, shouldScaleDownPreviousDeployment } = await promoteConfirmation({
      deployment,
      stableEnvironments: stableEnvironments.filter((env) => checkHasAccess(env.name)),
      hasAccessToProdEnv: checkHasAccess(PROD_ENV_NAME),
    });

    if (confirmed) {
      if (env === PROD_ENV_NAME) {
        promiseSnackbar(
          promoteDeploymentToProd({
            variables: {
              id: deployment.id,
            },
          }),
          ({ data }) =>
            `#${data.updateChainDeployment.chainDeployment.id} is being promoted to production.`,
          (err) =>
            `${getFirstGraphQLError(err)?.message ?? 'An unexpected error occurred'}. #${
              deployment.id
            } could not be promoted.`,
        );
      } else {
        promiseSnackbar(
          promoteDeploymentToEnvironment({
            variables: {
              chainDeploymentId: deployment.id,
              shouldScaleDownPreviousDeployment,
              stableEnvironmentName: env,
            },
          }),
          ({ data }) =>
            `#${data.promoteChainDeploymentToStableEnvironment.chainDeployment.id} is being promoted to ${env} environment.`,
          (err) =>
            `${getFirstGraphQLError(err)?.message ?? 'An unexpected error occurred'}. #${
              deployment.id
            } could not be promoted.`,
        );
      }
    }
  }, [
    deployment,
    promoteDeploymentToEnvironment,
    promiseSnackbar,
    promoteDeploymentToProd,
    promoteConfirmation,
    stableEnvironments,
    checkHasAccess,
  ]);
}

function useCancelChainDeploymentPromotion(
  progressRelease: ProductionReleasesForChainEnvironmentFragment,
  chainId: string,
) {
  const promiseSnackbar = usePromiseSnackbar();
  const confirmationDialog = useConfirmationDialog();
  const [cancelChainDeploymentPromotion] = useCancelChainDeploymentPromotionMutation({
    update: (cache: InMemoryCache) => {
      updateReleaseStatusInCache(
        cache,
        chainId,
        ChainEnvironmentReleaseStatus.RampingDown,
        progressRelease.stableEnvironmentName,
      );
    },
  });
  return useCallback(async () => {
    if (!progressRelease) {
      return;
    }

    const confirmed = await confirmationDialog({
      title: 'Cancel Promotion',
      okText: 'Cancel Promotion',
      prompt:
        progressRelease.stableEnvironmentName === PROD_ENV_NAME
          ? // eslint-disable-next-line max-len
            `You're cancelling a gradual traffic promotion. Traffic will slowly return to the previous deployment, which may take a few minutes.To stop traffic to the current deployment immediately, deactivate it instead.

            Are you sure you want to cancel the promotion?`
          : // eslint-disable-next-line max-len
            `You're cancelling a gradual traffic promotion. Traffic will slowly return to the previous ${progressRelease.stableEnvironmentName} deployment, which may take a few minutes.To stop traffic to the current deployment immediately, deactivate it instead.

            Are you sure you want to cancel the promotion?`,
      okColor: 'error',
      autoFocus: false,
      width: 700,
      cancelText: null,
    });

    if (confirmed) {
      promiseSnackbar(
        cancelChainDeploymentPromotion({
          variables: { chainDeploymentId: progressRelease.chainDeployment.id },
        }),
        `Promotion for deployment #${progressRelease.chainDeployment.id} is canceled.`,
        (err) =>
          `${getFirstGraphQLError(err)?.message ?? 'An unexpected error occurred'}. #${
            progressRelease.chainDeployment.id
          } promotion could not be canceled.`,
      );
    }
  }, [progressRelease, confirmationDialog, promiseSnackbar, cancelChainDeploymentPromotion]);
}

function useDeleteEnv(name: string) {
  const { chainId } = useParams<MatchParams>();
  const promiseSnackbar = usePromiseSnackbar();
  const safeguardConfirmationDialog = useSafeguardConfirmationDialog();
  const isTeamsEnabled = useIsTeamsEnabled();

  const [deleteEnv] = useDeleteChainStableEnvironmentMutation({
    update: (cache: InMemoryCache, { data }) => {
      cache.updateQuery(
        {
          query: ChainStableEnvironmentsDocument,
          variables: {
            chainId,
          },
          broadcast: false,
        },
        produce((data: ChainStableEnvironmentsQueryHookResult['data']) => {
          data.chainStableEnvironments = data.chainStableEnvironments.filter(
            (env) => env.name !== name,
          );
        }),
      );
    },
  });

  return useCallback(async () => {
    const failedText = `An unexpected error occurred. Environment ${name} could not be deleted.`;
    const variables = { name, chainId };

    const confirmed = await safeguardConfirmationDialog({
      title: 'Delete environment',
      okText: 'Delete',
      prompt: 'Are you sure you want to delete this environment? This cannot be undone.',
      validationText: name,
      validationHint: 'Environment name',
    });

    if (confirmed) {
      promiseSnackbar(
        deleteEnv({
          variables,
          refetchQueries: isTeamsEnabled ? ['Environments', 'EnvironmentGroups'] : [],
        }),
        'The environment was successfully deleted.',
        failedText,
      );
    }
  }, [deleteEnv, name, promiseSnackbar, chainId, safeguardConfirmationDialog, isTeamsEnabled]);
}

function useStableEnvironment(providedId?: string) {
  const { tabEntityId } = useParams<MatchParams>();
  const id = providedId || tabEntityId;
  const { data, loading } = useChainStableEnvironmentQuery({
    variables: {
      id,
    },
    fetchPolicy: 'cache-first',
    pollInterval: 10000,
    skip: id === PROD_ENV_NAME || !id,
  });

  return {
    stableEnvironment: data?.chainStableEnvironment,
    loading,
  };
}

function useProductionReleasesForChainEnvironment(envName: string) {
  const { chainId } = useParams<MatchParams>();
  const { data, loading, refetch } = useProductionReleasesForChainEnvironmentQuery({
    fetchPolicy: 'cache-first',
    variables: {
      chainId,
      envName,
    },
  });

  return {
    releases: data?.chainEnvironmentReleases
      ? sortBy(data.chainEnvironmentReleases, (release) => moment(release.created).valueOf())
          .reverse()
          .filter(
            (release) =>
              ![
                ChainEnvironmentReleaseStatus.Canceled,
                ChainEnvironmentReleaseStatus.Failed,
              ].includes(release.status),
          )
      : null,
    loading,
    refetch,
  };
}

function useConfigurePromotionEnvId() {
  const { chainId } = useParams<MatchParams>();

  return useRecoilState(configurePromotionEnvId(chainId));
}

function useRegisterChainBreadcrumbs() {
  const { chainId } = useParams<MatchParams>();
  const {
    urlId: { tabEntityId: deploymentId, chainletId },
  } = useUrlId();
  useRegisterBreadcrumbs([
    {
      entityType: 'Chain',
      id: chainId,
    },
    {
      entityType: 'ChainDeployment',
      id: deploymentId,
    },
    {
      entityType: 'Chainlet',
      id: chainletId,
    },
  ]);
}

function useChainDeployments(poll = false) {
  const { chainId } = useParams<MatchParams>();
  const { data, loading } = useChainDeploymentsQuery({
    variables: { id: chainId },
    fetchPolicy: 'cache-first',
    pollInterval: poll ? 10000 : null,
  });

  return {
    deployments: data?.chain.deployments,
    loading,
  };
}

function useChainDeploymentS3DownloadUrl(chainDeploymentId: string) {
  const [queryChainDeploymentS3DownloadUrl] = useChainDeploymentS3DownloadUrlLazyQuery({
    variables: {
      id: chainDeploymentId,
    },
  });

  return queryChainDeploymentS3DownloadUrl;
}

export {
  useDeleteChain,
  useUrlId,
  useChain,
  useChainlets,
  usePlaygroundId,
  useAutoScalingVersionId,
  useDeleteChainDeployment,
  useDeactivatChainDeployment,
  useActivateChainDeployment,
  useCurrentUsage,
  useWakeUpChainlet,
  usePromoteChainDeployment,
  useAPIEndpointId,
  useRenameDeploymentId,
  updateChainDeploymentNameInCache,
  // stable environments
  useStableEnvironments,
  useStableEnvironment,
  useCreateStableEnvironment,
  useGetDeploymentAssociatedEnv,
  usePromoteDeploymentToEnvironment,
  useCancelChainDeploymentPromotion,
  useProductionReleasesForChainEnvironment,
  useDeleteEnv,
  useConfigurePromotionEnvId,
  useRegisterChainBreadcrumbs,
  useChainDeployments,
  useChainDeploymentS3DownloadUrl,
};
